import { Link } from 'react-router-dom';
import { Search, BookOpen, FileText, Users, Wallet, ShieldCheck, ArrowRight, MessageCircle } from 'lucide-react';
import MarketingLayout from './shared/MarketingLayout';
import { PageHero, Section, SectionHeader, CTAStrip } from './shared/blocks';
import SEO from './shared/SEO';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { useState, useMemo } from 'react';

const ARTICLES = [
  { cat: 'Getting Started', icon: BookOpen, title: 'Creating your account & verifying email',     summary: 'Three-minute signup, email verification, first login.' },
  { cat: 'Getting Started', icon: BookOpen, title: 'Generating your first lesson plan',          summary: 'Pick subject, board, chapter — hit generate.' },
  { cat: 'AI Tools',        icon: FileText, title: 'Worksheet generator — difficulty mix',       summary: 'Control Easy/Medium/Hard ratios, set marks, generate.' },
  { cat: 'AI Tools',        icon: FileText, title: 'Question papers with blueprint upload',      summary: 'Match your school\'s template exactly.' },
  { cat: 'School ERP',      icon: Users,    title: 'Setting up classes and sections',            summary: 'Bulk import, manual add, class teacher assignment.' },
  { cat: 'School ERP',      icon: Wallet,   title: 'Fee structures & Razorpay integration',      summary: 'Installments, late-fee rules, receipt formats.' },
  { cat: 'Account',         icon: ShieldCheck, title: 'Two-factor authentication',               summary: 'Enable 2FA on your account for extra security.' },
  { cat: 'Account',         icon: ShieldCheck, title: 'Cancelling your subscription',            summary: 'How and when to cancel — and what data stays.' },
];

export default function HelpCenterPage() {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const t = q.toLowerCase().trim();
    if (!t) return ARTICLES;
    return ARTICLES.filter(a => (a.title + ' ' + a.summary + ' ' + a.cat).toLowerCase().includes(t));
  }, [q]);
  const grouped = useMemo(() => {
    const out = {};
    for (const a of filtered) (out[a.cat] = out[a.cat] || []).push(a);
    return out;
  }, [filtered]);

  return (
    <MarketingLayout>
      <SEO
        title="Help Center | Searchable knowledge base for teachers & admins"
        description="ActanonEdu Help Center — searchable how-to articles for teachers, admins and school leaders. Covers content engine, School ERP, account, billing, and integrations."
        path="/help-center"
        keywords={['ActanonEdu help center', 'AI teacher software help', 'school ERP support']}
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Help Center', url: '/help-center' }]}
      />
      <PageHero
        eyebrow="Help Center"
        title="How can we"
        accent="help?"
        subtitle="Search the knowledge base — or skip the search and WhatsApp the founder directly."
        testid="help-hero"
      >
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search articles..."
            className="pl-9 h-12 text-base bg-white dark:bg-mk-navy"
            data-testid="help-search"
          />
        </div>
      </PageHero>

      <Section>
        {Object.keys(grouped).length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500 mb-4">No articles match &ldquo;{q}&rdquo;.</p>
            <Link to="/contact"><Button variant="outline"><MessageCircle className="h-4 w-4 mr-1.5" /> Ask a human</Button></Link>
          </div>
        ) : (
          Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="mb-10" data-testid={`help-cat-${cat.toLowerCase().replace(/\s/g, '-')}`}>
              <SectionHeader eyebrow="Category" title={cat} />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((a, i) => (
                  <div key={i} className="rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-5 hover:shadow-lg transition-all cursor-pointer">
                    <a.icon className="h-5 w-5 text-mk-blue dark:text-mk-cyan mb-2" />
                    <div className="font-bold text-sm leading-snug">{a.title}</div>
                    <p className="text-sm text-mk-muted dark:text-slate-300 mt-1.5 leading-relaxed">{a.summary}</p>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </Section>

      <CTAStrip
        title="Cannot find what you need?"
        subtitle="WhatsApp the founder — fastest path to a human answer."
        primary={{ label: 'Contact us', href: '/contact' }}
        secondary={{ label: 'WhatsApp', wa: 'Hi! I have a question that\'s not in the help center.' }}
        testid="help-final-cta"
      />
    </MarketingLayout>
  );
}
